import React, { useEffect, useRef, useState } from 'react';
import { Book, Lightbulb, Heart, ChevronRight, ExternalLink, Feather, TreePine } from 'lucide-react';
import { NOTION_RESOURCES } from '../../constants/navigation';

const HomeSection = ({ handleNavigate, theme, isDarkMode }) => {
  const [btn1Visible, setBtn1Visible] = useState(false);
  const [btn2Visible, setBtn2Visible] = useState(false);
  const buttonsRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setBtn1Visible(true);
          setTimeout(() => setBtn2Visible(true), 200); // Stagger the second button by 200ms
          observer.disconnect();
        }
      },
      { threshold: 0.1 } // Triggers as soon as 10% of the container is visible
    );

    if (buttonsRef.current) {
      observer.observe(buttonsRef.current);
    }

    return () => observer.disconnect();
  }, []);
  
  // The Zen Hammer: Inline style to guarantee the pill shape
  const pillStyle = { borderRadius: '9999px' };

  return (
    <div className="animate-in fade-in duration-1000">
      
      {/* Hero Section */}
      <section className="pt-12 pb-24 px-4 min-h-[90vh] flex flex-col justify-center relative">
        <div className="max-w-5xl mx-auto flex flex-col items-center w-full">
          
          {/* THE TOP TAG: Personal Opinion & Wisdom */}
          <div 
            style={{ animationDelay: '150ms', animationFillMode: 'both' }}
            className={`text-[10px] font-bold uppercase tracking-[0.4em] mb-16 opacity-50 animate-in fade-in slide-in-from-bottom-2 duration-1000 ${theme.textSubtle}`}
          >
            Personal Opinion & Wisdom
          </div>
          
          {/* THE HERO HEADLINE: Fixed to match Image exactly */}
          <div className="w-full max-w-4xl flex flex-col items-center text-center">
            <img
              src="/profile-home-pen.png"
              alt="Ben Hasan in a monochrome watercolor portrait"
              style={{
                animationDelay: '300ms',
                animationFillMode: 'both',
                mixBlendMode: isDarkMode ? 'screen' : 'multiply',
                filter: isDarkMode ? 'invert(1) grayscale(1)' : 'grayscale(1)',
              }}
              className="w-48 md:w-56 h-auto object-contain mb-8 flex-shrink-0 animate-in fade-in zoom-in-90 duration-1000"
            />
            <h1 
              style={{ animationDelay: '450ms', animationFillMode: 'both' }}
              className={`text-3xl md:text-[50px] lg:text-[50px] font-serif font-medium tracking-tight leading-tight animate-in fade-in slide-in-from-bottom-3 duration-1000 ${theme.text}`}
            >
              Berfikir tentang apa sahaja
            </h1>
            <h2 
              style={{ animationDelay: '600ms', animationFillMode: 'both' }}
              className={`text-2xl md:text-[40px] lg:text-[40px] font-serif italic font-light tracking-tight leading-tight opacity-70 mt-2 md:mt-4 animate-in fade-in slide-in-from-bottom-3 duration-1000 ${theme.textSubtle}`}
            >
              yang terlintas laju.
            </h2>
          </div>
          
          {/* THE SUBTEXT: Menulis untuk memperlahan... */}
          <p 
            style={{ animationDelay: '800ms', animationFillMode: 'both' }}
            className={`mt-10 md:mt-16 text-base md:text-xl max-w-xs md:max-w-2xl text-center opacity-80 leading-relaxed font-serif italic tracking-wide animate-in fade-in slide-in-from-bottom-3 duration-1000 ${theme.textMuted}`}
          >
            Menulis untuk memperlahan apa yang terlintas itu.
          </p>
          
          {/* ACTION AREA */}
          <div ref={buttonsRef} className="mt-20 flex flex-col md:flex-row items-center justify-center gap-4 w-full px-6">
            
            <button 
              onClick={() => handleNavigate('blog')}
              style={pillStyle}
              className={`w-full md:w-auto px-10 py-4 flex items-center justify-center space-x-3 group transition-all duration-700 border ${
                isDarkMode 
                ? 'bg-stone-900/50 border-stone-800 text-stone-400 hover:text-white hover:bg-stone-800 hover:-translate-y-1' 
                : 'bg-stone-50/50 border-stone-200 text-stone-500 hover:text-stone-900 hover:bg-white hover:shadow-lg hover:-translate-y-1'
              } ${btn1Visible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-95'}`}
            >
              <Feather size={16} className="opacity-50 group-hover:opacity-100 transition-opacity" />
              <span className="text-[10px] font-bold uppercase tracking-[0.25em] mt-px">Explore Writings</span> 
            </button>

            <button 
              onClick={() => handleNavigate('quotes')}
              style={pillStyle}
              className={`w-full md:w-auto px-10 py-4 flex items-center justify-center space-x-3 group transition-all duration-700 border ${
                isDarkMode 
                ? 'bg-stone-900/50 border-stone-800 text-stone-400 hover:text-white hover:bg-stone-800 hover:-translate-y-1' 
                : 'bg-stone-50/50 border-stone-200 text-stone-500 hover:text-stone-900 hover:bg-white hover:shadow-lg hover:-translate-y-1'
              } ${btn2Visible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-95'}`}
            >
              <TreePine size={16} className="opacity-50 group-hover:opacity-100 transition-opacity" />
              <span className="text-[10px] font-bold uppercase tracking-[0.25em] mt-px">Sanctuary of Thought</span> 
            </button>

          </div>

          {/* COGITO ET SUM: Spaced out to match reference */}
          <div 
            style={{ animationDelay: '1000ms', animationFillMode: 'both' }}
            className={`mt-24 text-[9px] md:text-[10px] font-bold uppercase tracking-[0.8em] opacity-50 animate-in fade-in duration-1000 ${theme.textSubtle}`}
          >
            C O G I T O &nbsp; E T &nbsp; S U M
          </div>

          {/* Dynamic Scroll Indicator - A delicate needle looping downwards */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-40 hover:opacity-85 transition-opacity duration-300">
            <span className="text-[8px] font-bold uppercase tracking-[0.3em] opacity-50">Descend</span>
            <div className={`w-[1px] h-10 ${isDarkMode ? 'bg-stone-800' : 'bg-stone-200'} relative overflow-hidden`}>
              <div className="absolute top-0 left-0 w-full h-4 bg-stone-500 dark:bg-stone-400 animate-scroll-needle" />
            </div>
          </div>

        </div>
      </section>

      {/* Quote Section */}
      <section className="py-24">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center">
            <p className={`text-2xl md:text-4xl font-serif leading-snug mb-10 animate-in fade-in slide-in-from-bottom-4 duration-1000 delay-500 ${theme.text}`}>
              "The narrower the truth, <br className="hidden md:block" /> the wider the wisdom."
            </p>
            <div className="flex flex-col items-center">
              <div className={`w-8 h-px mb-6 ${isDarkMode ? 'bg-stone-800' : 'bg-stone-200'}`} />
              <cite className={`not-italic font-bold uppercase tracking-[0.4em] text-[10px] opacity-60 ${theme.textMuted}`}>
                — Ben Hasan
              </cite>
            </div>
          </div>
        </div>
      </section>

      {/* External Repositories */}
      <section className="py-32">
        <div className="max-w-4xl mx-auto px-6">
          <div className="mb-20 text-center">
            <h2 className={`text-4xl font-serif font-bold mb-4 ${theme.text}`}>External Repositories</h2>
            <p className={`text-sm tracking-[0.1em] opacity-60 uppercase ${theme.textMuted}`}>Digital archives on Notion</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {NOTION_RESOURCES.map((res, i) => {
              const isInternal = !!res.id;
              const CardTag = isInternal ? 'div' : 'a';
              const cardProps = isInternal 
                ? { onClick: () => handleNavigate(res.id) }
                : { href: res.link, target: '_blank', rel: 'noopener noreferrer' };

              return (
                <CardTag 
                  key={i} 
                  {...cardProps}
                  className={`group p-10 rounded-[40px] border transition-all duration-700 hover:-translate-y-2 cursor-pointer ${
                    isDarkMode 
                      ? 'bg-transparent border-stone-800 hover:border-stone-600 hover:bg-stone-900/40' 
                      : 'bg-white border-stone-100 hover:border-stone-200 hover:shadow-2xl hover:shadow-stone-200/40'
                  }`}
                >
                  <div 
                    style={pillStyle}
                    className={`mb-10 p-5 inline-block transition-all duration-700 ${
                    isDarkMode 
                      ? 'bg-stone-800 text-stone-300 group-hover:bg-stone-100 group-hover:text-stone-900' 
                      : 'bg-stone-50 text-stone-600 group-hover:bg-stone-900 group-hover:text-white'
                  }`}>
                    {i === 0 ? <Book size={22} /> : i === 1 ? <Lightbulb size={22} /> : <Heart size={22} />}
                  </div>
                  
                  <h3 className={`text-xl font-bold mb-4 flex items-center group-hover:translate-x-1 transition-transform ${theme.text}`}>
                    {res.title} 
                    {isInternal ? (
                      <ChevronRight size={14} className="ml-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    ) : (
                      <ExternalLink size={14} className="ml-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    )}
                  </h3>
                  
                  <p className={`text-sm leading-relaxed opacity-80 ${theme.textMuted}`}>{res.description}</p>
                </CardTag>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomeSection;
